/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import dayjs from "dayjs";
import { useCallback } from "react";
import {
    FormGroup, Input,
    Table
} from "reactstrap";
import useConfig, { useTenantFormOptions } from "../../../../actions/Tenants/config/configHook";
import { useFields } from "../../../../hooks";
import { FromLink } from "../../../../utils/useNavigateFrom";
import { Loading } from "../../../Helper/Loading";
import { TableCell, TableHeaderCell } from "../../../Helper/Table";
import { formatDate } from "../../Layers/fields/formatters";
import useRipeningRoom, { DATE_URL_FORMAT, LIST_OR_GRID, useIsItemAllocated } from "../hooks/useRipeningRoom";

// Table to show either the pallets or the groups within a PO
export default function RipeningRoomItems() {
    const config = useConfig();
    const form_options = useTenantFormOptions();
    const { ripeningActionAllowed, groups, pallets, listOrGrid, selectedLayers, cellRows, cellColumns, setSelectedLayers, deselectAll, groupAccentColors, fruit_type, fruit_type_config, checks, dateRange } = useRipeningRoom();
    const isItemAllocatedOnDate = useIsItemAllocated();
    const { applyDisplayFilterLayerFields, setupFieldClassFromConfig } = useFields();

    const fields = applyDisplayFilterLayerFields(
        config?.root_config?.ripening_room_pallet_fields,
        {
            fruit_type
        }
    );


    const today = dayjs();

    if (!pallets || !fields) {
        return <Loading />;
    }

    const values = (p) => fields.map((i, index) => <TableCell key={index}>{i.set_value({
        config,
        layer: p,
        check: null, // * To be used in check tables and layer overview
        fruit: null, // * Fruit list is not available on overview screen
        children: null, // * children is only available on layer index page
        form_options
    }).display_component()}</TableCell>);

    const header = () => fields.map((i, index) => <TableHeaderCell key={index}>{i.label}</TableHeaderCell>);


    const toggleSelectedLayer = (layer_id) => setSelectedLayers((prev) => ({ ...prev, [layer_id]: !prev[layer_id] }));

    // * Select all pallets (the one that are still in the ripening cell, on this date)
    const selectAll = useCallback(() => {
        setSelectedLayers(pallets.reduce((acc: Record<string, boolean>, i: any) => ({
            ...acc,
            [i.id]: isItemAllocatedOnDate(i.id, today),
        }), {}));
    }, [pallets, isItemAllocatedOnDate, today, setSelectedLayers]);

    const selectGroup = useCallback((groupKey: string, selected: boolean) => {
        setSelectedLayers((prev) => {
            const group = groups.find((g) => g.group_key === groupKey);
            if (!group) {
                return prev;
            }

            const next = { ...prev };
            group.items.forEach((item: any) => {
                next[item.id] = selected && isItemAllocatedOnDate(item.id, today);
            });

            return next;
        });
    }, [groups, setSelectedLayers, isItemAllocatedOnDate, today]);

    const allInGroupSelected = useCallback((groupKey: string) => {
        const group = groups.find((g) => g.group_key === groupKey);
        if (!group || group.items.length === 0) {
            return false;
        }
        return group.items.every((item: any) => selectedLayers[item.id] && isItemAllocatedOnDate(item.id, today));
    }, [groups, selectedLayers, isItemAllocatedOnDate, today]);


    // Check if all pallets have a selected layer and are allocated for today, ensuring the list is not empty
    const allSelected = pallets.length > 0 && pallets.every((i) => selectedLayers[i.id] && isItemAllocatedOnDate(i.id, today));

    // Handler to toggle select/deselect all
    const toggleSelectAll = () => {
        if (allSelected) {
            deselectAll();
        } else {
            selectAll();
        }
    };

    // const gridPallets = pallets.filter((p) => (listOrGrid === LIST_OR_GRID.GRID && p.cell_level === filterLevel));
    const cellIndexPalletId = pallets.map((i) => i.id);

    const ripeningFields = (fruit_type_config?.ripening_fields || [])
        .map((fieldConfig) => setupFieldClassFromConfig(fieldConfig, { fruit_type }))
        .filter((field) => field);

    const sortedChecks = checks.map((i) => ({ ...i, created_dayjs: dayjs(i.created) })).sort((a, b) => a.created_dayjs.valueOf() - b.created_dayjs.valueOf());

    const crossPatternSvg = encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><path d='M0 0 L8 8 M8 0 L0 8' stroke='silver' stroke-width='1' fill='none' stroke-linecap='square'/></svg>");
    const halfCrossPatternSvg = encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><path d='M0 0 L8 8' stroke='white' stroke-width='1' fill='none' stroke-linecap='square'/></svg>");
    const unallocatedBackground = css`
        background-color: #f8f9fa;
        background-image: url("data:image/svg+xml,${crossPatternSvg}");
        background-size: 8px 8px;
    `;
    const halfDayBackground = css`
        background-color: #f8f9fa;
        background-image: url("data:image/svg+xml,${halfCrossPatternSvg}");
        background-size: 8px 8px;
    `;

    let cellIndex = -1; // TODO: temporary Used to allocate pallets to cell locations (arbirtary without ERP)

    return <div >
        <div css={css`min-height: 300px;`}>
            { listOrGrid === LIST_OR_GRID.LIST && <Table size="sm" responsive={true} borderless={true}>
                <thead>
                    <tr>
                        {ripeningActionAllowed && <TableHeaderCell><Input checked={allSelected}
                            onChange={toggleSelectAll} type="checkbox" /></TableHeaderCell>}
                        {header()}
                        {dateRange.map((date, index) => (
                            <TableHeaderCell key={index} >
                                {formatDate(date)}
                            </TableHeaderCell>
                        ))}
                    </tr>
                </thead>
                {groups.map((group: any, index) => {
                    const groupHeaderColSpan = fields.length + dateRange.length;
                    return (
                        <tbody key={index} css={css`border-width: 1px 0px 1px 0px; border-type: solid; border-color: #323030;`}>
                            <tr className="bg-body-secondary">
                                {ripeningActionAllowed && (
                                    <TableCell>
                                        <FormGroup check>
                                            <Input
                                                type="checkbox"
                                                checked={allInGroupSelected(group.group_key)}
                                                onChange={() => selectGroup(group.group_key, !allInGroupSelected(group.group_key))}
                                            />
                                        </FormGroup>
                                    </TableCell>
                                )}
                                <TableCell colSpan={groupHeaderColSpan}>
                                    <div className="d-flex align-items-center gap-3 py-2">
                                        <strong>{group.group_label || "Ungrouped"}</strong>
                                        {typeof group.pallets === "number" && (
                                            <span className="text-muted small">{group.pallets} pallets</span>
                                        )}
                                        {typeof group.boxes === "number" && group.boxes > 0 && (
                                            <span className="text-muted small">{group.boxes} boxes</span>
                                        )}
                                    </div>
                                </TableCell>
                            </tr>
                            {group.items.map((pallet, indexx) => (
                                <tr key={indexx} title={pallet?.group_label}>
                                    {ripeningActionAllowed && <TableCell>
                                        <FormGroup check>
                                            <Input disabled={!isItemAllocatedOnDate(pallet.id, today)} onChange={() => toggleSelectedLayer(pallet.id)} checked={Boolean(selectedLayers[pallet.id]) && isItemAllocatedOnDate(pallet.id, today)} type="checkbox" />
                                        </FormGroup>
                                    </TableCell>}
                                    {values(pallet)}
                                    {dateRange.map((date, dateIndex) => {
                                        const dailyChecks = sortedChecks
                                            .filter((c) => c.layer_ids.includes(pallet.id) && c.created_dayjs.format(DATE_URL_FORMAT) === date.format(DATE_URL_FORMAT));

                                        const latestCheck = dailyChecks[dailyChecks.length - 1];

                                        const isAllocated = isItemAllocatedOnDate(pallet.id, date);
                                        const wasAllocatedYesterday = isItemAllocatedOnDate(pallet.id, date.subtract(1, "day"));
                                        let backgroundStyle: SerializedStyles | undefined;
                                        if (!isAllocated) {
                                            backgroundStyle = wasAllocatedYesterday ? halfDayBackground : unallocatedBackground;
                                        }
                                        return (
                                            <TableCell
                                                key={dateIndex}
                                                css={backgroundStyle}
                                                className={
                                                    isAllocated
                                                        ? "align-middle text-center bg-body-tertiary"
                                                        : "align-middle text-center bg-secondary-subtle text-muted"
                                                }
                                            >
                                                {latestCheck && (
                                                    <FromLink
                                                        to={`/layer/${latestCheck.layer_id}/add-check/${latestCheck.check_id}/summary`}
                                                    >
                                                        <div className="d-flex flex-column align-items-center gap-1">
                                                            {ripeningFields
                                                                .filter((field) => field.fieldname_getter !== "label")
                                                                .map((field, fieldIndex) => (
                                                                    <span key={fieldIndex} title={field.label}>
                                                                        {field.set_value({
                                                                            config,
                                                                            layer: pallet,
                                                                            check: latestCheck,
                                                                            fruit: null,
                                                                            children: null,
                                                                            form_options,
                                                                        }).display_component()}
                                                                    </span>
                                                                ))}
                                                        </div>
                                                    </FromLink>
                                                )}
                                            </TableCell>
                                        );
                                    })}

                                </tr>
                            ))}
                        </tbody>
                    );
                })}
            </Table > }
            { listOrGrid === LIST_OR_GRID.GRID && <div >
                {cellRows.map((row, rowIndex) => <div key={rowIndex} className="d-flex flex-nowrap pb-2 mx-n3 justify-content-center">
                    {cellColumns.map((column, columnIndex) => {
                        if (column.toLowerCase() === "hall") {
                            return <div key={columnIndex} className="px-3 mx-3" ></div>;
                        }

                        // TODO: temporary hack to allocate pallets to cell locations
                        cellIndex++; // Track pallet index globally


                        return <div key={columnIndex} className=" mx-3 d-flex" >
                            <div className="p-3 fw-bold text-end" css={css`min-width: 4rem;`}>
                                {column}{row}
                            </div>
                            <div css={css`min-width: 30rem; min-height: 4.5rem;`} className="bg-light">

                                {groups.flatMap((group: any) => group.items
                                    // .filter((p) => (listOrGrid === LIST_OR_GRID.GRID && p.cell_level === filterLevel))
                                    .filter((p) => cellIndexPalletId[cellIndex] === p.id)
                                    // .filter((p) => p.cell_row === row && p.cell_column === column) // * Filter pallets for this cell TODO: enable when ERP connection is Ready
                                    .map((p, indexx) => <div key={indexx} className="bg-white pb-3">
                                        <div className="border rounded-end p-3 shadow" css={[css`min-width: 30rem;border-width: 1px 1px 1px 10px!important;`, groupAccentColors[p.group_key]]}>
                                            <div className="d-flex align-items-center">
                                                <h5 className="mb-0 fw-bold">{p.label}</h5>
                                                <div className="ms-auto pe-3">
                                                    <FormGroup check>
                                                        <Input disabled={!isItemAllocatedOnDate(p.id, today)} onChange={() => toggleSelectedLayer(p.id)} checked={Boolean(selectedLayers[p.id]) && isItemAllocatedOnDate(p.id, today)} type="checkbox" />
                                                    </FormGroup>
                                                </div>
                                            </div>
                                            <table>
                                                {[...fields, ...ripeningFields].filter((i) => i && i.fieldname_getter !== "label").map((i, index) => {
                                                    const latestCheck = sortedChecks.find((c) => c.layer_ids.includes(p.id) && c.created_dayjs.format(DATE_URL_FORMAT) === dayjs().format(DATE_URL_FORMAT));
                                                    return <tr key={index}>
                                                        <td>{i.label}</td>
                                                        <td className="fw-bold ps-2">
                                                            {
                                                                i.set_value({
                                                                    config,
                                                                    layer: p,
                                                                    check: latestCheck, // * To be used in check tables and layer overview
                                                                    fruit: null, // * Fruit list is not available on overview screen
                                                                    children: null, // * children is only available on layer index page
                                                                    form_options
                                                                }).display_component()
                                                            }
                                                        </td>
                                                    </tr>;
                                                })}
                                            </table>
                                        </div>
                                    </div>))}
                            </div>
                        </div>;
                    })}
                </div>)}


            </div>
            }
        </div>

    </div>;

}
